import { applyMiddleware, createStore as originalCreateStore, Middleware, Store } from 'redux';
import sagaMiddlewareFactory from 'redux-saga';
import { isBefore, parseISO, startOfToday } from 'date-fns';

import { rootReducer, SearchFormState } from './reducers';
import rootSaga from './sagas';
import { get, SEARCH_FORM_STATE_KEY } from '../../cache';
import { setPassengers } from './passengers/actions';
import { setMultiCity, setSegments } from './segments/actions';
import { convertPassengersSearchParameters, convertSegmentsSearchParameters } from '../utils';
import memes from '../../memes';
import { CachedSearchFormState, PassengerTypeConfig } from '../types';
import { getLocale } from '../../utils';
import { setRedeemMiles } from './redeemMiles/actions';
import { PassengerConfigState } from './passengersConfig/reducers';
import { offerHotelsAction } from './offerHotels/actions';
import { SearchParametersFragment } from '@websky/graphql';
import { useConfig } from '../../context';

const parseCachedDate = (date: string | Date) => (typeof date === 'string' ? parseISO(date) : date);

const actualizeDate = (date: Date) => (isBefore(date, startOfToday()) ? startOfToday() : date);

export const createStore = memes(
	(
		defaultParameters?: SearchParametersFragment,
		passengersConfig?: PassengerTypeConfig[],
		proMode?: boolean,
		offerHotelsDefaultState?: boolean
	): Store<SearchFormState> => {
		const searchFormState = get<CachedSearchFormState>(SEARCH_FORM_STATE_KEY, {});

		const saga = sagaMiddlewareFactory({
			context: {
				config: useConfig()
			}
		});
		let defaultState: Partial<SearchFormState> = {};

		const middleware: Middleware[] = [saga];

		if (process.env.NODE_ENV === 'development') {
			middleware.push(require('redux-logger').default);
		}

		// Use cached state only if the cached locale is equal to the current user locale.
		if (searchFormState && searchFormState.locale === getLocale()) {
			// Convert strings from cache to Date
			if (searchFormState.state) {
				searchFormState.state.segments.multiCitySegments.forEach(segment => {
					segment.date = actualizeDate(parseCachedDate(segment.date));
					segment.dateBack = actualizeDate(parseCachedDate(segment.dateBack));
				});

				const simpleRoute = searchFormState.state.segments.simpleRouteSegment;

				simpleRoute.date = actualizeDate(parseCachedDate(simpleRoute.date));
				simpleRoute.dateBack = actualizeDate(parseCachedDate(simpleRoute.dateBack));
			}

			defaultState = searchFormState.state;
		}

		if (passengersConfig) {
			const passengersConfigState: PassengerConfigState = {};

			if (!defaultState) {
				defaultState = {};
			}

			if (!defaultState?.passengers) {
				defaultState.passengers = {};
			}

			passengersConfig.forEach(passenger => {
				let count = 0;
				if (defaultParameters?.passengers) {
					count =
						defaultParameters.passengers.find(pass => pass.passengerType === passenger.code)?.count || 0;
				} else if (passenger.defaultCount) {
					count = passenger.defaultCount;
				}
				if (
					defaultState.selectedFareGroup === passenger.group ||
					(defaultState.selectedFareGroup === 'Standard' && !passenger.group)
				) {
					defaultState.passengers[passenger.code] = count;
				} else if (!defaultState.passengers[passenger.code]) {
					defaultState.passengers[passenger.code] = 0;
				}

				passengersConfigState[passenger.code] = passenger;
			});

			// https://youtrack.websky.aero/issue/WEBSKY-6432#focus=Comments-4-113349.0-0
			for (const statePassengerCode of Object.keys(defaultState.passengers)) {
				const configPassenger = passengersConfig.find(
					configPassenger => configPassenger.code === statePassengerCode
				);

				if (!configPassenger) {
					defaultState.passengers[statePassengerCode] = 0;
				}
			}

			defaultState.passengersConfig = passengersConfigState;
		}

		const store = originalCreateStore(rootReducer, defaultState, applyMiddleware(...middleware));

		// Some predefined values.
		if (defaultParameters && defaultParameters.passengers) {
			store.dispatch(
				setPassengers(
					convertPassengersSearchParameters(defaultParameters.passengers, defaultState.passengersConfig)
				)
			);
		}

		if (proMode) {
			store.dispatch(setMultiCity(true));
		}

		store.dispatch(offerHotelsAction(!!offerHotelsDefaultState));

		if (defaultParameters?.ffpMode ?? searchFormState?.state?.redeemMiles) {
			store.dispatch(setRedeemMiles(true));
		} else {
			store.dispatch(setRedeemMiles(false));
		}

		saga.run(rootSaga);

		if (defaultParameters && defaultParameters.segments) {
			store.dispatch(setSegments(convertSegmentsSearchParameters(defaultParameters.segments)));
		}

		return store;
	}
);
